import TextInput from '~/components/TextInput';
import Select from '~/components/Select';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { QuestionSettings } from '~/lib/Questions/Questions.types';
import { formatCurrency } from '~/lib/CurrencyHelpers';

interface Adjustment {
    name: string;
    type: 'percentage' | 'fixed';
    value: number;
    operation: 'add' | 'subtract';
}

interface EditInvoiceSummaryFormProps {
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
}

const EditInvoiceSummaryForm = ({ settings, setSettings }: EditInvoiceSummaryFormProps) => {
    const adjustments: Adjustment[] = (settings?.adjustments as Adjustment[]) || [];

    const addAdjustment = () => {
        const newAdjustments = [
            ...adjustments,
            { name: '', type: 'percentage' as const, value: 0, operation: 'subtract' as const }
        ];
        setSettings({ ...settings, adjustments: newAdjustments });
    };

    const removeAdjustment = (index: number) => {
        const newAdjustments = adjustments.filter((_, i) => i !== index);
        setSettings({ ...settings, adjustments: newAdjustments });
    };

    const updateAdjustment = (index: number, field: keyof Adjustment, value: any) => {
        const newAdjustments = [...adjustments];
        newAdjustments[index] = {
            ...newAdjustments[index],
            [field]: value
        };
        setSettings({ ...settings, adjustments: newAdjustments });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="input-label">Discounts & Fees</div>
            </div>

            <div className="text-sm text-gray-600 mb-3">
                Optionally add discounts or fees that will be applied to the invoice subtotal.
            </div>

            {adjustments.map((adjustment, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
                    <div className="flex items-start gap-3">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <TextInput
                                    label="Name"
                                    value={adjustment.name}
                                    onChange={(value) => updateAdjustment(index, 'name', value)}
                                    placeholder="e.g., Early Bird Discount, Processing Fee"
                                />
                            </div>
                            <div>
                                <Select
                                    label="Operation"
                                    value={adjustment.operation}
                                    onChange={(value) => updateAdjustment(index, 'operation', value)}
                                    options={[
                                        { value: 'subtract', label: 'Discount' },
                                        { value: 'add', label: 'Add Fee' }
                                    ]}
                                />
                            </div>
                            <div>
                                <Select
                                    label="Type"
                                    value={adjustment.type}
                                    onChange={(value) => updateAdjustment(index, 'type', value)}
                                    options={[
                                        { value: 'percentage', label: 'Percentage (%)' },
                                        { value: 'fixed', label: 'Fixed Amount ($)' }
                                    ]}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <TextInput
                                    label={adjustment.type === 'percentage' ? 'Percentage' : 'Amount'}
                                    type="number"
                                    value={adjustment.value?.toString() || '0'}
                                    onChange={(value) => updateAdjustment(index, 'value', parseFloat(value) || 0)}
                                    min="0"
                                    step={adjustment.type === 'percentage' ? '1' : '0.01'}
                                    placeholder={adjustment.type === 'percentage' ? 'e.g., 10 for 10%' : 'e.g., 25.00'}
                                />
                                {adjustment.type === 'percentage' && adjustment.value > 0 && (
                                    <div className="text-sm text-gray-500 mt-1">
                                        {adjustment.value}% will be {adjustment.operation === 'subtract' ? 'subtracted from' : 'added to'} the subtotal
                                    </div>
                                )}
                                {adjustment.type === 'fixed' && adjustment.value > 0 && (
                                    <div className="text-sm text-gray-500 mt-1">
                                        {formatCurrency(adjustment.value)} will be {adjustment.operation === 'subtract' ? 'subtracted from' : 'added to'} the subtotal
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => removeAdjustment(index)}
                            className="text-red-600 hover:text-red-800 p-2 mt-6"
                            title="Remove adjustment"
                        >
                            <TrashIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            ))}

            <button
                type="button"
                onClick={addAdjustment}
                className="btn-light-pill text-sm flex items-center gap-1"
            >
                <PlusIcon className="w-4 h-4" />
                Add Discount/Fee
            </button>
        </div>
    );
};

export default EditInvoiceSummaryForm;

